<script setup>
import AlertBox from '@js/components/alerts/AlertBox.vue';
import DialogDrawer from '@js/components/dialogs/DialogDrawer.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import TextareaField from '@js/components/fields/TextareaField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError } from '@js/utils/toast';
import moment from 'moment';
import { computed, reactive, ref } from 'vue';
import { boolean, date, object, string } from 'yup';

const { route } = useRoute();

const contentTypes = [
    {
        value: 'standard',
        label: 'Standard',
    },
    {
        value: 'premium',
        label: 'Premium',
    },
    {
        value: 'useless',
        label: 'Inutile',
    },
];

const props = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    eCommerceProduct: {
        type: Object,
        required: true,
    },
    seoApikomContent: {
        type: Object,
        required: false,
    },
    callback: {
        type: Function,
        required: false,
    },
});

const isOpen = ref(false);

const isSubmitting = ref(false);

const form = reactive({
    contentType: props.eCommerceProduct.seoContentProduct
        ? contentTypes.find(
              (contentType) =>
                  contentType.value ===
                  props.eCommerceProduct.seoContentProduct.contentType.value,
          )
        : props.seoApikomContent
          ? contentTypes.find(
                (contentType) =>
                    contentType.value === props.seoApikomContent.type.value,
            )
          : contentTypes[0],
    name: props.eCommerceProduct.languageName,
    shortDescription: props.eCommerceProduct.languageShortDescription,
    description: props.eCommerceProduct.languageDescription,
    descriptionUpdated: false,
    descriptionUpdatedAt: moment().format('YYYY-MM-DD'),
    metaTitle: props.eCommerceProduct.languageMetaTitle,
    metaDescription: props.eCommerceProduct.languageMetaDescription,
    metaUpdated: false,
    metaUpdatedAt: moment().format('YYYY-MM-DD'),
});

const isUseless = computed(() => form.contentType.value === 'useless');

const validationSchema = object({
    contentType: object()
        .shape({
            value: string().required('Ce champ est obligatoire.'),
            label: string().required('Ce champ est obligatoire.'),
        })
        .required('Ce champ est obligatoire.'),
    name: string().required('Ce champ est obligatoire.'),
    shortDescription: string().nullable(),
    description: string().nullable(),
    descriptionUpdated: boolean(),
    descriptionUpdatedAt: date().nullable(),
    metaTitle: string().nullable(),
    metaDescription: string().nullable(),
    metaUpdated: boolean(),
    metaUpdatedAt: date().nullable(),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route(
                'front.projectManagement.seo.postUpdateECommerceProductContents',
                {
                    tenant: props.tenant.id,
                },
            ),
            {
                seoContentProductContentType: form.contentType.value,
                cmsProductId: props.eCommerceProduct.cmsProductId,
                name: form.name,
                updateDescription: !isUseless.value && form.descriptionUpdated,
                shortDescription: form.shortDescription,
                description: form.description,
                seoContentProductDescriptionUpdatedAt:
                    !isUseless.value && form.descriptionUpdated
                        ? form.descriptionUpdatedAt
                        : null,
                updateMeta: !isUseless.value && form.metaUpdated,
                metaTitle: form.metaTitle,
                metaDescription: form.metaDescription,
                seoContentProductMetaUpdatedAt:
                    !isUseless.value && form.metaUpdated
                        ? form.metaUpdatedAt
                        : null,
            },
        )
        .then(() => {
            isOpen.value = false;

            if (props.callback) {
                props.callback();
            }
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);
        });
}
</script>

<template>
    <DialogDrawer v-model="isOpen" size="almost-full">
        <template #trigger>
            <slot name="trigger" />
        </template>

        <template #title> Mettre à jour </template>

        <template #full-content>
            <FormRoot
                :validation-schema="validationSchema"
                :submit="submit"
                v-slot="{ errors }"
            >
                <FormColumn>
                    <AlertBox
                        v-if="
                            (props.seoApikomContent &&
                                props.seoApikomContent.type.value !==
                                    form.contentType.value) ||
                            (props.eCommerceProduct.seoApikomContent &&
                                props.eCommerceProduct.seoApikomContent.type
                                    .value !== form.contentType.value)
                        "
                        type="danger"
                    >
                        Les types de contenu ne sont pas les mêmes.
                    </AlertBox>

                    <AlertBox v-if="isUseless" type="warning">
                        Ce contenu est marqué comme inutile : rien n'est envoyé
                        au CMS, il n'est pas compté dans les objectifs et il
                        n'apparaît pas côté client.
                    </AlertBox>

                    <FormContent>
                        <ListboxField
                            v-model="form.contentType"
                            field-id="contentType"
                            field-name="contentType"
                            label="Type de contenu"
                            :options="contentTypes"
                            by="value"
                            required
                        >
                            <template #buttonContent>
                                {{ form.contentType.label }}
                            </template>

                            <template #optionContent="{ option }">
                                {{ option.label }}
                            </template>
                        </ListboxField>

                        <InputField
                            v-model="form.name"
                            label="Nom"
                            field-name="name"
                            field-id="name"
                            required
                        />
                    </FormContent>

                    <FormContent v-if="!isUseless">
                        <SwitchField
                            v-model="form.metaUpdated"
                            label="Mettre à jour les Meta"
                            field-name="metaUpdated"
                            field-id="metaUpdated"
                        />

                        <InputField
                            v-if="form.metaUpdated"
                            v-model="form.metaUpdatedAt"
                            label="Meta mises à jour le"
                            field-name="metaUpdatedAt"
                            field-id="metaUpdatedAt"
                            type="date"
                        />

                        <InputField
                            v-model="form.metaTitle"
                            label="Méta-titre"
                            field-name="metaTitle"
                            field-id="metaTitle"
                            :disabled="!form.metaUpdated"
                            required
                        />

                        <TextareaField
                            v-model="form.metaDescription"
                            label="Méta-description"
                            field-name="metaDescription"
                            field-id="metaDescription"
                            :disabled="!form.metaUpdated"
                        />
                    </FormContent>

                    <FormContent v-if="!isUseless">
                        <SwitchField
                            v-model="form.descriptionUpdated"
                            label="Mettre à jour la description"
                            field-name="descriptionUpdated"
                            field-id="descriptionUpdated"
                        />

                        <InputField
                            v-if="form.descriptionUpdated"
                            v-model="form.descriptionUpdatedAt"
                            label="Description mise à jour le"
                            field-name="descriptionUpdatedAt"
                            field-id="descriptionUpdatedAt"
                            type="date"
                        />

                        <EditorField
                            v-model="form.description"
                            label="Description"
                            field-name="description"
                            field-id="description"
                            :disabled="!form.descriptionUpdated"
                            size="small"
                        />

                        <EditorField
                            v-model="form.shortDescription"
                            label="Description courte"
                            field-name="shortDescription"
                            field-id="shortDescription"
                            :disabled="!form.descriptionUpdated"
                            size="small"
                        />
                    </FormContent>
                </FormColumn>

                <FormFooter
                    :is-submitting="isSubmitting"
                    :errors="errors"
                    without-back-button
                />
            </FormRoot>
        </template>
    </DialogDrawer>
</template>
